import { Grid, CardHeader, CardContent, Box, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, Checkbox, ListItemText } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { subAdminServcie } from "../../services/AllServices";
import { availablePermissions } from "./availablePermission";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import errorHandler from "../../utils/errorHandler";
import { subadminSchema } from "../../utils/Schema";
import { EMAIL_REGEX } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import { trimFormValues } from "../../utils/helpers";

function AddEditSubadmin() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [data, setData] = useState(null);

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await subAdminServcie.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  useEffect(() => {
    if (id && data?.permissions) {
      setPermissions(data.permissions);
    }
  }, [id, data]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: subadminSchema(!!id),
    initialValues: {
      name: data?.name || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      permissions: Object.keys(data?.permissions || {}) || [],
      password: "",
      confirmPassword: "",
    },
    validate: (values) => {
      const errors = {};
      const email = values.email.trim();

      if (!email) {
        errors.email = "This field is required";
      } else if (!EMAIL_REGEX.test(email)) {
        errors.email = "Invalid email address";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the form values
        const trimmedValues = trimFormValues(values);
        
        const permissionsObject = Object.keys(permissions).reduce(
          (acc, permission) => {
            acc[permission] = permissions[permission];
            return acc;
          },
          {}
        );
        const payload = {
          id: id || undefined,
          name: trimmedValues.name,
          email: trimmedValues.email,
          phoneNumber: trimmedValues.phoneNumber,
          password: trimmedValues.password,
          confirmPassword: trimmedValues.confirmPassword,
          status: 1,
          permissions: permissionsObject,
        };

        const response = id
          ? await subAdminServcie.edit(payload)
          : await subAdminServcie.add(payload);
        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } = formik;

  // Update formik permissions when permissions state changes
  useEffect(() => {
    if (Object.keys(permissions).length > 0) {
      setFieldValue("permissions", Object.keys(permissions));
    }
  }, [permissions, setFieldValue]);
  const handleEmailChange = (event) => {
    const { value } = event.target;

    const trimmedValue = value.replace(/^\s+|\s+(?=\s)|\s+$/g, "");
    formik.setFieldValue("email", trimmedValue);
  };
  return (
    <Box>
      <title>{id ? "Edit" : "Add"} Subadmin</title>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Subadmin`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 6 }} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={formik.values.email}
                    onChange={handleEmailChange}
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    required
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }} md={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Permissions"
                    name="permissions"
                    value={values.permissions}
                    onChange={(e) => {
                      const selectedPermissions = e.target.value;
                      setPermissions(
                        selectedPermissions.reduce((acc, permission) => {
                          acc[permission] = true;
                          return acc;
                        }, {})
                      );
                      formik.setFieldTouched("permissions", true);
                      formik.setFieldValue("permissions", selectedPermissions);
                    }}
                    error={Boolean(
                      formik.touched.permissions && formik.errors.permissions
                    )}
                    helperText={
                      formik.touched.permissions && formik.errors.permissions
                    }
                    SelectProps={{

                      multiple: true,
                      renderValue: (selected) => selected.join(", "),
                    }}
                    sx={{
                      // Customize input font size
                      '& .MuiInputBase-input': {
                        fontSize: '13.8px', // Adjust as needed
                      },
                      // Customize label font size
                      '& .MuiInputLabel-root': {
                        fontSize: '14px',
                      },
                      // Customize helper text font size
                      '& .MuiFormHelperText-root': {
                        fontSize: '12px',
                      },
                    }}
                  >
                    {Object.keys(availablePermissions).map((permission) => (
                      <MenuItem key={permission} value={permission}>
                        <Checkbox
                          checked={values.permissions.includes(permission)}
                        />
                        <ListItemText primary={permission} />
                      </MenuItem>
                    ))}
                  </TextField>



                </Grid>
                {
                  values.permissions && values.permissions.length > 0 &&
                  <Grid size={{ xs: 12 }} md={12}>
                    <Box
                      sx={{
                        marginTop: "12px",
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        // backgroundColor: "#f9f9f9",
                        fontSize: "14px",
                        width: "100%"
                      }}
                    >
                      <strong>Selected Permissions:</strong>{" "}
                      {values.permissions && values.permissions.length > 0
                        ? values.permissions.join(", ")
                        : "None"}
                    </Box>
                  </Grid>
                }
                {!id && (
                  <>
                 <Grid size={{ xs: 6 }} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    required
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    error={Boolean(touched.password && errors.password)}
                    helperText={touched.password && errors.password}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm Password"
                    required
                    name="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>
                </>
                )}
                <Grid size={{ xs: 12 }} md={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Sub Admin
                    </LoadingButton>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }} md={12}></Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditSubadmin;
